/**
 * ParametrosValidacaoForm — edição dos limiares de validação (RF-M3-006).
 * Validação local antes do PATCH (distância ≥ 0; confiança em [0,1]; faixa ≥ 0; os dois
 * limiares da escola pelo nome em [0,1] — ADR-034; período da campanha com início ≤ fim, ano
 * assumido inteiro de 2000 a 2100 e os dois limiares do município pelo nome em [0,1] —
 * ADR-035; limite de custo do reprocessamento ≥ 0 — ADR-036); consome a API real via useParametros/useAtualizarParametros (sem mocks). As datas da
 * campanha vão à API em AAAAMMDD e aparecem no campo de data como AAAA-MM-DD.
 */

import { FormEvent, useEffect, useState } from 'react';
import { useAtualizarParametros, useParametros } from '../hooks/useBasesReferencia';
import { PatchParametros } from '../services/bases-types';

interface CamposForm {
  distanciaMaxKm: string;
  confiancaMinima: string;
  faixaAmpliadaMpLimite: string;
  escolaNomeSimilaridadeMin: string;
  escolaNomeFolgaMin: string;
  campanhaInicio: string;
  campanhaFim: string;
  campanhaAnoPadrao: string;
  municipioNomeSimilaridadeMin: string;
  municipioNomeFolgaMin: string;
  confirmarReprocessamentoAcimaUsd: string;
}

const VAZIO: CamposForm = {
  distanciaMaxKm: '',
  confiancaMinima: '',
  faixaAmpliadaMpLimite: '',
  escolaNomeSimilaridadeMin: '',
  escolaNomeFolgaMin: '',
  campanhaInicio: '',
  campanhaFim: '',
  campanhaAnoPadrao: '',
  municipioNomeSimilaridadeMin: '',
  municipioNomeFolgaMin: '',
  confirmarReprocessamentoAcimaUsd: '',
};

/** 20130101 → "2013-01-01" (valor do campo de data). */
export function aaaammddParaCampo(valor: number): string {
  const s = String(Math.trunc(valor)).padStart(8, '0');
  return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
}

/** "2013-01-01" → 20130101; texto vazio ou fora do formato → null. */
export function campoParaAaaammdd(texto: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(texto);
  return m ? Number(`${m[1]}${m[2]}${m[3]}`) : null;
}

const entreZeroEUm = (x: number): boolean => Number.isFinite(x) && x >= 0 && x <= 1;

/** Valida os campos e devolve o patch (apenas campos alterados) ou um erro. */
function validar(campos: CamposForm): { patch: PatchParametros } | { erro: string } {
  const distancia = Number(campos.distanciaMaxKm);
  const confianca = Number(campos.confiancaMinima);
  const faixa = Number(campos.faixaAmpliadaMpLimite);
  const similaridade = Number(campos.escolaNomeSimilaridadeMin);
  const folga = Number(campos.escolaNomeFolgaMin);
  if (!Number.isFinite(distancia) || distancia < 0) {
    return { erro: 'Distância máxima deve ser um número ≥ 0.' };
  }
  if (!Number.isFinite(confianca) || confianca < 0 || confianca > 1) {
    return { erro: 'Confiança mínima deve estar entre 0 e 1.' };
  }
  if (!Number.isFinite(faixa) || faixa < 0) {
    return { erro: 'Limite de faixa ampliada deve ser um número ≥ 0.' };
  }
  if (!entreZeroEUm(similaridade)) {
    return { erro: 'Semelhança mínima do nome da escola deve estar entre 0 e 1.' };
  }
  if (!entreZeroEUm(folga)) {
    return { erro: 'Folga mínima sobre a 2ª escola deve estar entre 0 e 1.' };
  }
  const inicio = campoParaAaaammdd(campos.campanhaInicio);
  const fim = campoParaAaaammdd(campos.campanhaFim);
  if (inicio === null || fim === null) {
    return { erro: 'Informe o início e o fim da campanha.' };
  }
  if (inicio > fim) {
    return { erro: 'O início da campanha não pode ser depois do fim.' };
  }
  const ano = Number(campos.campanhaAnoPadrao);
  if (!Number.isInteger(ano) || ano < 2000 || ano > 2100) {
    return { erro: 'Ano assumido deve ser um ano inteiro entre 2000 e 2100.' };
  }
  const simMunicipio = Number(campos.municipioNomeSimilaridadeMin);
  const folgaMunicipio = Number(campos.municipioNomeFolgaMin);
  if (campos.municipioNomeSimilaridadeMin === '' || !entreZeroEUm(simMunicipio)) {
    return { erro: 'Semelhança mínima do nome do município deve estar entre 0 e 1.' };
  }
  if (campos.municipioNomeFolgaMin === '' || !entreZeroEUm(folgaMunicipio)) {
    return { erro: 'Folga mínima sobre o 2º município deve estar entre 0 e 1.' };
  }
  const limiteCusto = Number(campos.confirmarReprocessamentoAcimaUsd);
  if (campos.confirmarReprocessamentoAcimaUsd === '' || !Number.isFinite(limiteCusto) || limiteCusto < 0) {
    return { erro: 'O limite de custo do reprocessamento deve ser um número ≥ 0.' };
  }
  return {
    patch: {
      distanciaMaxKm: distancia,
      confiancaMinima: confianca,
      faixaAmpliadaMpLimite: faixa,
      escolaNomeSimilaridadeMin: similaridade,
      escolaNomeFolgaMin: folga,
      campanhaInicio: inicio,
      campanhaFim: fim,
      campanhaAnoPadrao: ano,
      municipioNomeSimilaridadeMin: simMunicipio,
      municipioNomeFolgaMin: folgaMunicipio,
      confirmarReprocessamentoAcimaUsd: limiteCusto,
    },
  };
}

export function ParametrosValidacaoForm(): JSX.Element {
  const { parametros, carregando, erro } = useParametros();
  const { atualizar, salvando, erro: erroSalvar } = useAtualizarParametros();
  const [campos, setCampos] = useState<CamposForm>(VAZIO);
  const [erroLocal, setErroLocal] = useState<string | null>(null);
  const [salvo, setSalvo] = useState(false);

  useEffect(() => {
    if (parametros) {
      setCampos({
        distanciaMaxKm: String(parametros.distanciaMaxKm),
        confiancaMinima: String(parametros.confiancaMinima),
        faixaAmpliadaMpLimite: String(parametros.faixaAmpliadaMpLimite),
        escolaNomeSimilaridadeMin: String(parametros.escolaNomeSimilaridadeMin),
        escolaNomeFolgaMin: String(parametros.escolaNomeFolgaMin),
        campanhaInicio: aaaammddParaCampo(parametros.campanhaInicio),
        campanhaFim: aaaammddParaCampo(parametros.campanhaFim),
        campanhaAnoPadrao: String(parametros.campanhaAnoPadrao),
        municipioNomeSimilaridadeMin: String(parametros.municipioNomeSimilaridadeMin),
        municipioNomeFolgaMin: String(parametros.municipioNomeFolgaMin),
        confirmarReprocessamentoAcimaUsd: String(parametros.confirmarReprocessamentoAcimaUsd),
      });
    }
  }, [parametros]);

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setSalvo(false);
    const resultado = validar(campos);
    if ('erro' in resultado) {
      setErroLocal(resultado.erro);
      return;
    }
    setErroLocal(null);
    try {
      await atualizar(resultado.patch);
      setSalvo(true);
    } catch {
      /* erroSalvar cobre o feedback */
    }
  };

  const setCampo = (chave: keyof CamposForm) => (e: React.ChangeEvent<HTMLInputElement>): void => {
    setCampos((c) => ({ ...c, [chave]: e.target.value }));
    setSalvo(false);
  };

  return (
    <section className="card">
      <h2>Parâmetros de validação</h2>
      <p className="muted">
        Limiares usados pelo Motor de Validação. Alterações passam a valer na próxima validação,
        sem novo deploy.
      </p>

      {carregando && <p className="muted">Carregando parâmetros…</p>}
      {erro && <p className="notice">Não foi possível carregar os parâmetros.</p>}

      {!carregando && !erro && (
        <form onSubmit={onSubmit}>
          <div className="form-field">
            <label htmlFor="distanciaMaxKm">Distância geográfica máxima (km)</label>
            <input
              id="distanciaMaxKm"
              type="number"
              step="0.1"
              min="0"
              value={campos.distanciaMaxKm}
              onChange={setCampo('distanciaMaxKm')}
            />
          </div>

          <div className="form-field">
            <label htmlFor="confiancaMinima">Confiança mínima da leitura (0 a 1)</label>
            <input
              id="confiancaMinima"
              type="number"
              step="0.01"
              min="0"
              max="1"
              value={campos.confiancaMinima}
              onChange={setCampo('confiancaMinima')}
            />
          </div>

          <div className="form-field">
            <label htmlFor="faixaAmpliadaMpLimite">Limite de faixa ampliada (megapixels)</label>
            <input
              id="faixaAmpliadaMpLimite"
              type="number"
              step="0.1"
              min="0"
              value={campos.faixaAmpliadaMpLimite}
              onChange={setCampo('faixaAmpliadaMpLimite')}
            />
          </div>

          <fieldset className="form-fieldset">
            <legend>Escola pelo nome no município</legend>
            <p className="muted">
              Sem código INEP utilizável, a escola é procurada pelo nome lido entre as do município.
              Só é identificada quando o nome é parecido o bastante e se destaca da segunda mais
              parecida; senão, fica uma observação com as opções.
            </p>
            <div className="form-field">
              <label htmlFor="escolaNomeSimilaridadeMin">Semelhança mínima do nome da escola (0 a 1)</label>
              <input
                id="escolaNomeSimilaridadeMin"
                type="number"
                step="0.01"
                min="0"
                max="1"
                value={campos.escolaNomeSimilaridadeMin}
                onChange={setCampo('escolaNomeSimilaridadeMin')}
              />
            </div>
            <div className="form-field">
              <label htmlFor="escolaNomeFolgaMin">Folga mínima sobre a 2ª escola (0 a 1)</label>
              <input
                id="escolaNomeFolgaMin"
                type="number"
                step="0.01"
                min="0"
                max="1"
                value={campos.escolaNomeFolgaMin}
                onChange={setCampo('escolaNomeFolgaMin')}
              />
            </div>
          </fieldset>

          <fieldset className="form-fieldset">
            <legend>Período da campanha</legend>
            <p className="muted">
              Data do termo fora do período fica como está, com uma observação. Data sem ano recebe o
              ano assumido.
            </p>
            <div className="form-field">
              <label htmlFor="campanhaInicio">Início da campanha</label>
              <input
                id="campanhaInicio"
                type="date"
                value={campos.campanhaInicio}
                onChange={setCampo('campanhaInicio')}
              />
            </div>
            <div className="form-field">
              <label htmlFor="campanhaFim">Fim da campanha</label>
              <input id="campanhaFim" type="date" value={campos.campanhaFim} onChange={setCampo('campanhaFim')} />
            </div>
            <div className="form-field">
              <label htmlFor="campanhaAnoPadrao">Ano assumido quando a data não tem ano</label>
              <input
                id="campanhaAnoPadrao"
                type="number"
                step="1"
                min="2000"
                max="2100"
                value={campos.campanhaAnoPadrao}
                onChange={setCampo('campanhaAnoPadrao')}
              />
            </div>
          </fieldset>

          <fieldset className="form-fieldset">
            <legend>Município pelo nome (sem UF)</legend>
            <p className="muted">
              Sem UF resolvida, o município é procurado pelo nome lido em todo o país. Só é aceito
              quando é parecido o bastante e se destaca do segundo mais parecido; a UF passa a vir dele.
            </p>
            <div className="form-field">
              <label htmlFor="municipioNomeSimilaridadeMin">Semelhança mínima do nome do município (0 a 1)</label>
              <input
                id="municipioNomeSimilaridadeMin"
                type="number"
                step="0.01"
                min="0"
                max="1"
                value={campos.municipioNomeSimilaridadeMin}
                onChange={setCampo('municipioNomeSimilaridadeMin')}
              />
            </div>
            <div className="form-field">
              <label htmlFor="municipioNomeFolgaMin">Folga mínima sobre o 2º município (0 a 1)</label>
              <input
                id="municipioNomeFolgaMin"
                type="number"
                step="0.01"
                min="0"
                max="1"
                value={campos.municipioNomeFolgaMin}
                onChange={setCampo('municipioNomeFolgaMin')}
              />
            </div>
          </fieldset>

          <fieldset className="form-fieldset">
            <legend>Reprocessamento</legend>
            <p className="muted">
              Acima desse custo estimado, "Ler a imagem de novo" pede uma segunda confirmação.
            </p>
            <div className="form-field">
              <label htmlFor="confirmarReprocessamentoAcimaUsd">Confirmar reprocessamento acima de (US$)</label>
              <input
                id="confirmarReprocessamentoAcimaUsd"
                type="number"
                step="0.01"
                min="0"
                value={campos.confirmarReprocessamentoAcimaUsd}
                onChange={setCampo('confirmarReprocessamentoAcimaUsd')}
              />
            </div>
          </fieldset>

          {erroLocal && <p className="notice">{erroLocal}</p>}
          {erroSalvar && <p className="notice">Não foi possível salvar. Tente novamente.</p>}
          {salvo && <p className="notice notice-info">Parâmetros salvos.</p>}

          <div className="row-actions">
            <button type="submit" className="btn btn-primary" disabled={salvando}>
              {salvando ? 'Salvando…' : 'Salvar parâmetros'}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
